import { retryBaseQuery } from '@/utils/redux-hooks'

import { createApi } from '@reduxjs/toolkit/query/react'

// Single shared RTK Query API. All feature APIs attach their endpoints via
// `baseApi.injectEndpoints(...)` so they share one cache, one tag namespace,
// and one entry in the redux store. Cross-feature invalidation works
// automatically — e.g. a customer mutation invalidating `'Customers'` will
// refetch any query in any feature that provided `'Customers'`.
const tagTypes = [
  'CompanyReview',
  'OnboardingJourneys',
  'Company',
  'PropertyInstalls',
  'PropertyRacks',
  'CaptureReviewQueue',
  'CaptureTemplates',
  'CaptureRules',
  'SiteData',
  'TaskPage',
  'VisitSnapshots',
  'ContactReview',
  'ActivityCensus',
  'TextLinks',
  'AccountingCaseInvoices',
  'ApInvoices',
  'ApApprovalSettings',
  'ApWorkflows',
  'TechnicianPayments',
  'TechnicianCompanies',
  'CustomerInvoices',
  'ServiceCenter',
  'ServiceMute',
  'Accounts',
  'AdminQueue',
  'AlertCatalog',
  'Alerts',
  'AppConfig',
  'NetworkBuild',
  'NetworkVlans',
  'DhcpScopes',
  'AssignedToUsers',
  'AssignmentGroups',
  'AssistAvailability',
  'AssistConversation',
  'AssistQueue',
  'Attachments',
  'AttentionQueue',
  'AuditLog',
  'AuthMe',
  'CableModems',
  'CaseAiAnalysis',
  'CaseEquipmentOrders',
  'Shipments',
  'CaseSlas',
  'Cases',
  'ChannelImpact',
  'Channels',
  'Clients',
  'Contacts',
  'CustomerAiAnalysis',
  'CustomerFeatures',
  'Customers',
  'DashboardLayout',
  'DashboardTemplate',
  'WirelessRollup',
  'Deployments',
  'DeploymentsInvoices',
  'DeploymentMilestoneWorklist',
  'DesignIntake',
  'Cancellation',
  'CancellationAudit',
  'CancellationRecipients',
  'DesignIntakeTemplates',
  'Devices',
  'DispatchInvoices',
  'DispatchStats',
  'DispatchTasks',
  'CostApprovals',
  'DispatchTechnicians',
  'DispatchWorkOrders',
  'Emails',
  'EngagementCarousel',
  'EngagementChannelAssignments',
  'EngagementDeployments',
  'EngagementMedia',
  'EngagementPlaylists',
  'EngagementPromotions',
  'EquipmentRequests',
  'EquipmentTypes',
  'TechnicianQuestionnaires',
  'TechnicianSchedule',
  'TechnicianInvoices',
  'TechnicianNotes',
  'TechnicianStats',
  'TechnicianTasks',
  'TechnicianTeam',
  'FieldValues',
  'Files',
  'FileTags',
  'GlobalSearch',
  'Groups',
  'HubCasting',
  'Icons',
  'Incidents',
  'Initiatives',
  'Interactions',
  'Invitations',
  'IspCircuits',
  'Issues',
  'LgService',
  'Lineup',
  'LogisticsAssets',
  'LogisticsBoard',
  'DesignTasks',
  'ApDesigner',
  'ChangeOrders',
  'LogisticsFaultCodes',
  'LogisticsModels',
  'SoftwareStandards',
  'LogisticsReports',
  'LogisticsShipments',
  'LogisticsShipmentCounts',
  'LogisticsInFlight',
  'LogisticsPostedShipments',
  'LogisticsInbound',
  'LogisticsInventory',
  'LogisticsTestProfiles',
  'Marketing',
  'Metrics',
  'KnowledgePage',
  'Models',
  'NotificationCounts',
  'NotificationPreferences',
  'Notifications',
  'Opportunities',
  'OrganizationMembers',
  'Organizations',
  'OtaCoverage',
  'PermissionGrants',
  'Permissions',
  'PmsConfig',
  'PmsData',
  'PmsStatus',
  'PmsConfiguration',
  'Preferences',
  'PriceBooks',
  'PriceCategories',
  'PriceImports',
  'Pricing',
  'PricingApiKeys',
  'Properties',
  'Reports',
  'Reservations',
  'RolePermissions',
  'Roles',
  'RoomData',
  'ServerStatus',
  'SiteConfig',
  'SiteSurveys',
  'SpecialNotes',
  'Submissions',
  'Surveys',
  'TechEquipmentTypes',
  'TechSkillTypes',
  'TechnicianSkillsEquipment',
  'Technicians',
  'Themes',
  'Timeline',
  'TlxFiles',
  'UserProjectMappings',
  'UserRoles',
  'Users',
  'VendorIps',
  'Vendors',
  'VendorTypes',
  'VendorFieldDefinitions',
  'VideoConfigs',
  'VideoList',
  'VideoUpload',
  'VoiceAgentSettings',
  'Weather',
  'CaseContacts',
  'WorkOrderRequests',
  'WorkOrderTasks',
  'WorkOrders',
  'Zones'
] as const

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: retryBaseQuery,
  tagTypes,
  endpoints: () => ({})
})
